import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { createApiClient, type ApiClient } from './client';

const LS_BASE_URL = 'synkadmin.apiBaseUrl';

// Rydd bort token-overstyring lagret av tidligere versjoner.
localStorage.removeItem('synkadmin.tokenOverride');

interface ApiContextValue {
    client: ApiClient;
    /** Konfigurert base-URL mot forvaltning-api. */
    baseUrl: string;
    setBaseUrl: (url: string) => void;
    /** true når et AGOL-token er tilgjengelig. */
    hasToken: boolean;
}

const ApiContext = createContext<ApiContextValue | null>(null);

export interface ApiProviderProps {
    /** AGOL-token fra IdentityManager (via useSynkAuth). */
    agolToken: string | undefined;
    /** Default base-URL fra app-innstillinger. */
    defaultBaseUrl: string;
    children: React.ReactNode;
}

export const ApiProvider: React.FC<ApiProviderProps> = ({ agolToken, defaultBaseUrl, children }) => {
    const [baseUrl, setBaseUrlState] = useState<string>(
        () => localStorage.getItem(LS_BASE_URL) || defaultBaseUrl,
    );

    // Hold token i en ref slik at klienten alltid leser ferskeste verdi uten
    // å rekonstrueres (og dermed invalidere React Query-cachen) ved refresh.
    const tokenRef = useRef<string | undefined>(agolToken);
    tokenRef.current = agolToken;

    const setBaseUrl = useCallback((url: string) => {
        setBaseUrlState(url);
        localStorage.setItem(LS_BASE_URL, url);
    }, []);

    // Klienten rekonstrueres kun når base-URL endres. getToken leser ref.
    const client = useMemo(
        () =>
            createApiClient({
                baseUrl,
                getToken: () => tokenRef.current,
            }),
        [baseUrl],
    );

    const hasToken = !!agolToken;

    const value = useMemo<ApiContextValue>(
        () => ({ client, baseUrl, setBaseUrl, hasToken }),
        [client, baseUrl, setBaseUrl, hasToken],
    );

    return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>;
};

export function useApi(): ApiContextValue {
    const ctx = useContext(ApiContext);
    if (!ctx) throw new Error('useApi må brukes innenfor <ApiProvider>');
    return ctx;
}
